import { ListStore, memone, SingleIndex, VoidCallback } from '@deqo/foundation';
import { EventRouter, EventSink, EventSource } from '@deqo/podopolis';
import { injectable } from 'inversify';
import { BoardParticipantData } from '../../../api/ApiTypes';
import { Client, ClientManager } from '../../../concepts/objects';
import { resolve } from '../../Container';
import { EditorSession } from '../EditorSession';
import { MetadataProvider } from '../MetadataProvider';
import { MyProfileInfo } from '../MyProfileInfo';
import { Participant } from './Participant';

export interface ParticipantManagerStreams {
    join: Participant;
    leave: Participant;
    update: Participant;
}

type PMS = ParticipantManagerStreams;

@injectable()
export class ParticipantManager extends ListStore<Participant> implements EventSource<PMS> {

    @resolve(ClientManager) private clients: ClientManager;
    @resolve(MetadataProvider) private provider: MetadataProvider;
    @resolve(EditorSession) private session: EditorSession;

    private events = new EventRouter<PMS>();
    private tracker = new SingleIndex<string, string>();

    protected async start() {
        // Start loading initial participant list
        await this.loadInitialParticipants();
        // Start tracking the trackable
        this.clients.all.forEach(x => this.onClientJoin(x));
        // Hook dem events
        this.burden(
            this.clients.on('join', this.onClientJoin),
            this.clients.on('leave', this.onClientLeave),
            this.clients.on('update', this.onClientUpdate),
        );
    }

    public get(userId: string) {
        const inst = this.getParticipant(userId);
        return inst?.loaded ? inst : null;
    }

    public getOrLoad(userId: string) {
        const ptpt = this.getParticipant(userId);
        if (!!ptpt) {
            return ptpt;
        }
        else {
            // Allocate a shell participant
            const shell = Participant.shell(userId);
            this.updateState(s => s.push(shell));
            this.refreshParticipant(userId);
            return shell;
        }
    }

    public getOrFallback(userId: string) {
        if (userId === null) return Participant.anonymous;
        return this.get(userId) ?? Participant.unknown;
    }

    public getSelf() {
        return this.find(x => x.self);
    }

    public online() {
        return this.filter(x => x.id !== 'US000000ANON' && x.presence !== 'offline');
    }

    public on(type: '*', to: EventSink<Participant, string>): VoidCallback;
    public on<K extends keyof PMS>(type: K, to: EventSink<PMS[K], K>): VoidCallback;
    public on(type: any, to: any) {
        return this.events.on(type, to);
    }

    public async refreshParticipant(userId: string) {
        if (!this.getParticipant(userId)) return;
        return this.loadParticipant(userId, true);
    }

    private onClientJoin = (client: Client) => {
        // If client not marked as track, which is probably always false on join, ignore
        if (client.profile.track) {
            this.startTracking(client);
        }
    };

    private onClientLeave = (client: Client) => {
        this.stopTracking(client);
    };

    private onClientUpdate = ([client, affectedKeys]: [Client, string[]]) => {
        if (affectedKeys.includes('track')) {
            if (client.profile.track) {
                this.startTracking(client);
            }
            else {
                this.stopTracking(client);
            }
        }
        else if (affectedKeys.includes('hat')) {
            this.getParticipant(client.userId)?.update({ hat: client.get('hat') ?? '' });
        }
    };

    private startTracking(client: Client) {
        this.tracker.add(client.userId, client.id);
        // Set participant to joined if tracking, so they show up in the strip
        const extra = client.profile.track ? { joined: true } : null;
        if (!this.updateParticipant(client.userId, extra)) {
            // If a participant was not loaded, then load
            this.loadParticipant(client.userId).then(() => {
                this.updateParticipant(client.userId, { joined: true });
            });
        }
    }

    private stopTracking(client: Client) {
        if (!this.tracker.get(client.userId).has(client.id)) return;
        this.tracker.remove(client.userId, client.id);
        this.updateParticipant(client.userId);
    }

    private async loadInitialParticipants() {
        const data = await this.provider.fetchParticipantData();
        const ptpts = data.map(x => this.createParticipant(x));
        this.updateState(s => s.push(...ptpts));
        data.forEach(x => this.updateParticipant(x.id, { loaded: true }));
    }

    private getParticipant(userId: string) {
        return this.index(this.getState()).get(userId);
    }

    private async loadParticipant(userId: string, forceRefresh?: boolean) {
        // If already loaded, just call update and stop
        if (this.getParticipant(userId) && !forceRefresh) {
            this.updateParticipant(userId);
        }
        else {
            const data = await this.provider.fetchParticipantData(userId);
            if (data) {
                // Create if not exists
                if (!this.getParticipant(userId)) {
                    this.updateState(s => s.push(this.createParticipant(data)));
                }
                this.updateParticipant(userId, { ...data, loaded: true });
            }
        }
    }

    private createParticipant(data: BoardParticipantData) {
        const me = this.session.get(MyProfileInfo);
        const self = me.id === data.id;
        const ptpt = new Participant(data, self);
        this.burden(ptpt.observe((n, o) => {
            if (n.presence === 'active' && o.presence !== 'active') {
                this.events.emit('join', ptpt);
            }
            this.events.emit('update', ptpt);
            if (n.presence === 'offline' && o.presence !== 'offline') {
                this.events.emit('leave', ptpt);
            }
        }));
        return ptpt;
    }

    private updateParticipant(userId: string, latestData?: Partial<BoardParticipantData>): boolean {
        const ptpt = this.getParticipant(userId);
        if (!ptpt) return false;
        const presence = ptpt.self || this.tracker.get(userId).size > 0 ? 'active' : 'offline';
        const hat = this.resolveHat(userId);
        ptpt.update({ presence, hat, ...(latestData || {}) });
        return true;
    }

    private resolveHat(userId): string {
        for (const clientId of this.tracker.get(userId)) {
            const hat = this.clients.get(clientId)?.get('hat');
            if (hat) return hat;
        }
        return '';
    }

    @memone()
    private index(list: Participant[]) {
        return new Map(list.map(p => [p.id, p]));
    }
}
